import { useTranslation } from "react-i18next";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { Loader2 } from "lucide-react";

import { EvosCoin } from "../../../assets/EvosCoin";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Separator } from "../../../components/ui/separator";
import { Skeleton } from "../../../components/ui/skeleton";
import { cn } from "../../../lib/utils";
import { formatPrice } from "../../../utils/getPrice";
import { OrderCalculation } from "../services/useOrderCalculation";

const formatPoints = (value: number) => (value ? formatPrice(value) : "0");

/** «i» из макета, 16px, цвет подсказки. */
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <circle cx="8" cy="8" r="6.67" stroke="#878C98" strokeWidth="1.2" />
    <path d="M8 7.33V11.33" stroke="#878C98" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="8" cy="5.33" r="0.67" fill="#878C98" />
  </svg>
);

type Props = {
  calculation: OrderCalculation;
  isPickupMode: boolean;
  className?: string;
};

/**
 * Блок расчёта заказа: «Списать баллы», промокод и строки итога. Стоит только
 * на чекауте — данные из useOrderCalculation.
 *
 * В плашке «Списать баллы» — не баланс, а сколько Mindbox разрешает списать
 * на эту корзину (discountAmountForCurrentOrder). Тумблер списывает ровно эту
 * сумму: меньшую выбрать нельзя (макет 1996-83797).
 */
export const OrderCalculationCard = ({
  calculation,
  isPickupMode,
  className,
}: Props) => {
  const { t } = useTranslation();
  const {
    isAuth,
    isCalculatingCart,
    promo,
    points,
    summary,
  } = calculation;

  const hasPromoStatus = promo.isValid || promo.isInvalid;
  const showValueSkeleton = isAuth && isCalculatingCart;

  // Функция, а не вложенный компонент: тип компонента, объявленный в
  // рендере, React пересоздаёт и перемонтирует на каждом обновлении.
  const renderValue = (
    children: React.ReactNode,
    valueClassName?: string,
    width = "w-20",
  ) =>
    showValueSkeleton ? (
      <Skeleton className={cn("h-4 rounded-md", width)} />
    ) : (
      <p
        className={cn(
          "text-body-base-regular text-text-primary",
          valueClassName,
        )}
      >
        {children}
      </p>
    );

  return (
    <Card
      className={cn(
        "px-1 pt-1 pb-3 flex flex-col space-y-4 rounded-2xl",
        className,
      )}
    >
      <div className="flex flex-col space-y-1"></div>
      <div className="px-3 flex flex-col space-y-[14px]">
        <div className="flex flex-col space-y-2">
          <div className="flex flex-col gap-2 rounded-xl bg-[#F0F2F3] px-3 py-[10px]">
            <div className="flex min-h-7 flex-row items-center justify-between gap-3">
              <p className="text-[14px] font-medium leading-5 text-[#0B0D0F]">
                {t("points-remove")}
              </p>
              <div className="flex flex-row items-center gap-3">
                {showValueSkeleton ? (
                  <Skeleton className="h-[23px] w-16 rounded-full" />
                ) : (
                  <div className="flex h-[23px] items-center gap-[5px] rounded-full bg-brand-green py-[2px] pl-[7px] pr-[3px]">
                    <span className="font-inter-tight text-[13px] font-semibold leading-4 text-white">
                      {formatPoints(points.available)}
                    </span>
                    <EvosCoin className="h-[17px] w-[17px]" />
                  </div>
                )}
                <SwitchPrimitives.Root
                  checked={points.enabled}
                  disabled={points.isDisabled}
                  onCheckedChange={points.onToggle}
                  aria-label={t("points-remove")}
                  className="inline-flex h-7 w-16 shrink-0 cursor-pointer select-none touch-manipulation items-center rounded-full p-[2px] transition-colors [-webkit-tap-highlight-color:transparent] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-brand-green data-[state=unchecked]:bg-[#78787833]"
                >
                  <SwitchPrimitives.Thumb className="pointer-events-none block h-6 w-[39px] rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.12)] transition-transform data-[state=checked]:translate-x-[21px] data-[state=unchecked]:translate-x-0" />
                </SwitchPrimitives.Root>
              </div>
            </div>
            {/* Процент — с бэкенда (mindbox_bonus_payment_percent), не из
                кода: лимит меняется в Mindbox без релиза. Не пришёл — «-»
                вместо числа, чем врать старым значением. */}
            <div className="flex flex-row items-center gap-2">
              <InfoIcon />
              <p className="text-[12px] font-medium leading-[14px] text-[#878C98]">
                {points.isBlockedByPromo
                  ? t("points-blocked-by-promo")
                  : t("points-limit-hint", {
                      percent: points.limitPercent ?? "-",
                    })}
              </p>
            </div>
          </div>

          <div className="flex flex-col space-y-3 rounded-2xl bg-fill-background p-3">
            <p className="text-[14px] font-normal leading-7 text-text-primary">
              {t("promocode")}
            </p>
            <div
              className={cn(
                "flex h-14 flex-row items-center rounded-[24px] border border-border bg-fill-base px-5",
                {
                  "border-brand-green bg-brand-green/10": promo.isValid,
                  "border-red-500 bg-red-500/10": promo.isInvalid,
                },
              )}
            >
              <input
                value={promo.value}
                onChange={(event) => promo.onChange(event.target.value)}
                placeholder={t("promocode")}
                disabled={promo.isBlockedByPoints}
                className="min-w-0 flex-1 bg-transparent text-[14px] font-normal leading-7 text-text-primary outline-none placeholder:text-text-tertiary disabled:opacity-50"
              />
              <Button
                className={cn(
                  "h-10 min-w-[92px] rounded-[20px] px-4 text-body-base-semibold shadow-none",
                  {
                    "bg-button-secondary text-text-primary": !hasPromoStatus,
                    "bg-brand-green text-text-white": promo.isValid,
                    "bg-fill-danger text-text-white": promo.isInvalid,
                  },
                )}
                // Отменить код можно всегда; применить — только без баллов.
                disabled={
                  isCalculatingCart ||
                  (!hasPromoStatus && promo.isBlockedByPoints)
                }
                onClick={hasPromoStatus ? promo.onCancel : promo.onApply}
              >
                {isCalculatingCart ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : hasPromoStatus ? (
                  t("cancel")
                ) : (
                  t("apply")
                )}
              </Button>
            </div>
            {promo.isValid ? (
              <p className="px-1 text-caption-xs-semibold text-brand-green">
                {t("promo-applied")}
              </p>
            ) : null}
            {promo.isInvalid ? (
              <p className="px-1 text-caption-xs-semibold text-red-500">
                {t("promo-invalid")}
              </p>
            ) : null}
            {promo.isBlockedByPoints && !hasPromoStatus ? (
              <div className="flex flex-row items-center gap-2 px-1">
                <InfoIcon />
                <p className="text-[12px] font-medium leading-[14px] text-[#878C98]">
                  {t("promo-blocked-by-points")}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {isPickupMode ? null : (
          <div className="flex flex-row items-center justify-between">
            <p className="text-body-base-regular text-text-tertiary">
              {t("delivery")}
            </p>
            {renderValue(
              <>
                {formatPrice(summary.deliveryPrice)} {t("sum")}
              </>,
            )}
          </div>
        )}
        {points.redeemed ? (
          <div className="flex flex-row items-center justify-between">
            <p className="text-body-base-regular text-text-tertiary">
              {t("points-redeemed")}
            </p>
            {renderValue(
              <>
                {formatPrice(points.redeemed)} {t("sum")}
              </>,
            )}
          </div>
        ) : null}
        <div className="flex flex-row items-center justify-between">
          <p className="text-body-base-regular text-text-tertiary">
            {t("amount-of-order")}
          </p>
          {renderValue(
            <>
              {formatPrice(summary.subtotal)} {t("sum")}
            </>,
          )}
        </div>
        {summary.promotionDiscount ? (
          <div className="flex flex-col space-y-2">
            {summary.promotions.map((promotion, index) => (
              <div
                className="flex flex-row items-center justify-between"
                key={`${promotion.name}-${index}`}
              >
                <p className="text-body-base-regular text-text-tertiary">
                  {promotion.name || t("promocode")}
                </p>
                {renderValue(
                  <>
                    -{formatPrice(promotion.amount)} {t("sum")}
                  </>,
                  "text-brand-green",
                  "w-16",
                )}
              </div>
            ))}
          </div>
        ) : null}
        {summary.backendTotalDiscount ? (
          <div className="flex flex-row items-center justify-between">
            <p className="text-body-base-regular text-text-tertiary">
              {t("discount")}
            </p>
            {renderValue(
              <>
                -{formatPrice(summary.backendTotalDiscount)} {t("sum")}
              </>,
              "text-brand-green",
              "w-16",
            )}
          </div>
        ) : null}
        {summary.earnedAmount ? (
          <div className="flex flex-row items-center justify-between">
            <p className="text-body-base-regular text-text-tertiary">
              {t("earned-points")}
            </p>
            {renderValue(
              <>
                +{formatPrice(summary.earnedAmount)} {t("point")}
              </>,
              "text-brand-green",
              "w-16",
            )}
          </div>
        ) : null}
        <Separator />

        <div className="flex flex-row items-center justify-between">
          <p className="text-body-base-regular text-text-tertiary">
            {t("total")}
          </p>
          {showValueSkeleton ? (
            <Skeleton className="h-5 w-24 rounded-md" />
          ) : (
            <p className="text-body-base-medium text-text-primary font-semibold">
              {formatPrice(summary.lastPrice)} {t("sum")}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};
